import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface FileNameModalProps {
  isOpen: boolean;
  originalFileName: string;
  originalFileNames?: string[]; // Support for multiple files
  saveDataName: string;
  setSaveDataName: (name: string) => void;
  onClose: () => void;
  onSave: () => Promise<void>;
  setError: (msg: string) => void;
  isLoading: boolean;
}

const FileNameModal: React.FC<FileNameModalProps> = ({
  isOpen,
  originalFileName,
  originalFileNames,
  saveDataName,
  setSaveDataName,
  onClose,
  onSave,
  setError,
  isLoading
}) => {
  const { t } = useTranslation('modals');
  const inputRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = useState<string>("");
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  
  // Auto-focus input when modal opens and set default name
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setValidationError("");
      
      // Don't set any default filename - let user enter it manually
      
      // Focus input after animation starts
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select(); // Select all text for easy editing
        }
      }, 100);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Validation function
  const validateFileName = (name: string): string => {
    const trimmed = name.trim();
    if (!trimmed) {
      return t('fileNameModal.validation.required', 'File name is required');
    }
    if (trimmed.length < 2) {
      return t('fileNameModal.validation.tooShort', 'File name must be at least 2 characters');
    }
    if (trimmed.length > 100) {
      return t('fileNameModal.validation.tooLong', 'File name must be less than 100 characters');
    }
    // Check for invalid characters
    const invalidChars = /[<>:"/\\|?*]/;
    const hasControlChars = trimmed.split('').some(char => char.charCodeAt(0) < 32);
    if (invalidChars.test(trimmed) || hasControlChars) {
      return t('fileNameModal.validation.invalidChars', 'File name contains invalid characters');
    }
    return "";
  };

  // Handle input change with live validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSaveDataName(value);

    // Show validation error live so the user can see WHY the Save button is
    // disabled (path traversal chars, too-short name, invalid chars, etc.).
    // Empty string is the "fresh / about to type" state — don't show a
    // "required" error before the user has typed anything.
    if (value.trim().length === 0) {
      setValidationError("");
    } else {
      setValidationError(validateFileName(value));
    }
  };

  if (!isOpen) return null;

  const handleSave = async () => {
    const error = validateFileName(saveDataName);
    if (error) {
      setValidationError(error);
      return;
    }

    try {
      await onSave();
      // Don't call onClose() here - let the parent component manage modal state
      // This allows multi-file save workflows where the modal reopens for the next file
    } catch (error) {
      console.error("Error saving file:", error);
      setError(
        error instanceof Error ? error.message : t('fileNameModal.errors.saveFailed', 'Error saving file')
      );
    }
  };

  // Handle Enter key on input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleSave();
    }
  };

  const isValidName = !validateFileName(saveDataName);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="filename-modal-title"
      className={`fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ${
        isAnimating ? 'bg-black bg-opacity-50' : 'bg-black bg-opacity-0'
      }`}
    >
      <div className={`bg-card rounded-xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 ${
        isAnimating ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'
      }`}>
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="filename-modal-title" className="text-base sm:text-lg font-semibold text-foreground">
                  {t('fileNameModal.title', 'Save File')}
                </h3>
                <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground leading-tight">
                  {t('fileNameModal.subtitle', 'Choose a name for your processed data')}
                </p>
              </div>
            </div>
            {!isLoading && (
              <button
                onClick={onClose}
                className="p-1.5 sm:p-2 hover:bg-muted rounded-full transition-colors flex-shrink-0"
                aria-label={t('close', { ns: 'common', defaultValue: 'Close' })}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          {/* Original file info */}
          <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-muted rounded-lg">
            <div className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-muted-foreground">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>
                {originalFileNames && originalFileNames.length > 1 
                  ? `Original files (${originalFileNames.length}):`
                  : t('fileNameModal.originalFile', 'Original file:')
                }
              </span>
            </div>
            {originalFileNames && originalFileNames.length > 1 ? (
              <div className="mt-1 space-y-1">
                {originalFileNames.map((fileName, index) => (
                  <p key={index} className="font-medium text-foreground truncate text-xs sm:text-sm">
                    {index + 1}. {fileName}
                  </p>
                ))}
              </div>
            ) : (
              <p className="mt-1 font-medium text-foreground truncate text-xs sm:text-sm">{originalFileName}</p>
            )}
          </div>

          {/* File name input */}
          <div className="space-y-1.5 sm:space-y-2">
            <label htmlFor="fileName" className="block text-xs sm:text-sm font-medium text-foreground">
              {t('fileNameModal.fileName', 'File Name')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="fileName"
                ref={inputRef}
                type="text"
                value={saveDataName}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className={`w-full px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${
                  validationError
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : isValidName && saveDataName
                    ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                    : 'border-input focus:ring-ring focus:border-ring'
                }`}
                placeholder={t('fileNameModal.placeholder', 'Enter file name')}
                disabled={isLoading}
                maxLength={100}
              />
              <div className="absolute right-2 sm:right-3 top-2 sm:top-3">
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-primary border-t-transparent"></div>
                ) : validationError ? (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : isValidName && saveDataName ? (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : null}
              </div>
            </div>
            
            {/* Validation error */}
            {validationError && (
              <p className="text-xs sm:text-sm text-red-600 flex items-center space-x-1">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{validationError}</span>
              </p>
            )}
            
            {/* Character count */}
            <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground">
              <span>{t('fileNameModal.extension', 'File will be saved as CSV')}</span>
              <span>{saveDataName.length}/100</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-muted rounded-b-xl flex justify-end space-x-2 sm:space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('fileNameModal.buttons.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!isValidName || isLoading}
            className="px-4 py-1.5 sm:px-6 sm:py-2 text-xs sm:text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5 sm:space-x-2"
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-white border-t-transparent"></div>
            )}
            <span>
              {isLoading ? t('fileNameModal.buttons.saving', 'Saving...') : t('fileNameModal.buttons.save', 'Save')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileNameModal;
